import { scoreRingColor } from "./scoring";

/**
 * Entity-keyed color lookups for "round-shape" dimensions across stats panels
 * (#1729). These are bounded, small-cardinality numeric dimensions where the
 * SAME value must always render the SAME color, no matter what subset/order a
 * filter produces — unlike color-by-array-index, which reassigns colors when
 * the set of present values shifts (e.g. removing a 3-arrow series shifts a
 * 6-arrow series from index 1 to index 0, changing its color).
 *
 * One remaining dimension, drawing from a shared palette:
 *   - round_length (total arrows in a round): EndProfileCard "by round length"
 *
 * `arrows_per_end` used to be colored the same way for EndDistributionCard/
 * Chart, but #1768/#1769 moved that chart to score-based ring colors instead
 * (see `avgScoreRingColor` below) — its x-axis is a score scale, so coloring
 * by an unrelated categorical dimension (format) carried no score information
 * and, incidentally, could never agree with EndProfileCard's round_length
 * coloring for the same physical round. `arrowsPerEndColor` is kept here
 * (unused by EndDistributionCard now) in case another panel needs it.
 *
 * Tokens only — same 5 tokens previously duplicated independently as
 * SERIES_COLORS in EndDistributionChart.jsx and EndProfileCard.jsx.
 *
 * #2074 — this is now the app's ONLY categorical chart palette. It previously
 * competed with a second, different 5-colour series (`--cinder`, `--field`,
 * `--gold-text`, `--fg-mid`, `--fg-low`) declared twice more, in
 * `pages/Stats.jsx` as DISTANCE_COLORS and in `lib/trendSeries.js` as
 * SERIES_COLORS — so two adjacent panels on the Stats page coloured their
 * series from two unrelated ramps. Both are gone; both read from here.
 *
 * The slots themselves are `--chart-cat-1..5` in tokens.css, which is where
 * the choice of hue and the reasoning behind the ordering lives. Keep this
 * array a plain list of those tokens: it exists so JS-side consumers can
 * index the palette (recharts takes colours as props, not as CSS), NOT as a
 * second place to decide what the colours are.
 *
 * CATEGORICAL, and only that. Hue here means "a different series", never
 * "a better score" — score quality is `avgScoreRingColor` below, and marks
 * drawn on a target face take neither (fixed styles from svgDots.js, #1849).
 */
export const CATEGORICAL_PALETTE = [
  "var(--chart-cat-1)",
  "var(--chart-cat-2)",
  "var(--chart-cat-3)",
  "var(--chart-cat-4)",
  "var(--chart-cat-5)",
];

// Thomas Wang-style integer hash — scrambles bits across multiple xor/multiply
// rounds so a bare `% length` on the result doesn't alias structurally-related
// inputs onto the same bucket. A plain `(n - 1) % length` looked fine for
// arrows-per-end (1-12) but broke for round_length: common archery round
// sizes (30, 60, 72, 90, 120, 144...) are overwhelmingly multiples of 5 or
// 10, so nearly every one landed on the same palette slot — reported as
// "everything is shades of orange" once real round-length data hit it.
function mix32(x) {
  x = (x ^ (x >>> 16)) >>> 0;
  x = Math.imul(x, 0x45d9f3b) >>> 0;
  x = (x ^ (x >>> 16)) >>> 0;
  x = Math.imul(x, 0x45d9f3b) >>> 0;
  return (x ^ (x >>> 16)) >>> 0;
}

function paletteColor(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return CATEGORICAL_PALETTE[0];
  return CATEGORICAL_PALETTE[mix32(Math.round(n)) % CATEGORICAL_PALETTE.length];
}

export function arrowsPerEndColor(arrowsPerEnd) {
  return paletteColor(arrowsPerEnd);
}

export function roundLengthColor(roundLength) {
  return paletteColor(roundLength);
}

// Both callers (EndDistributionCard, EndProfileCard) cap their displayed
// series at CATEGORICAL_PALETTE.length (5), but the value domain itself is
// unbounded (any round length, any arrows-per-end) — so even a well-mixed
// hash will occasionally give two DIFFERENT values in the SAME displayed set
// the same color (e.g. 15 and 60-arrow rounds both hashing to --warning).
// Since every caller here shows at most `palette.length` series at once,
// resolve collisions within that one displayed set: keep each value's
// natural hash color the first time it's seen, and bump any later value
// that collides to the next unused palette slot. Values are expected
// pre-sorted by the caller (ascending), so the smaller/more "natural" value
// in a colliding pair keeps its hash color and the larger one is bumped —
// deterministic for a given set, at the cost of strict color invariance
// across different active sets (an acceptable tradeoff: a given archer's
// top-N round lengths/arrows-per-end rarely changes between views).
export function assignDistinctColors(values, colorFn) {
  const used = new Set();
  return values.map((v) => {
    const natural = colorFn(v);
    if (!used.has(natural)) {
      used.add(natural);
      return natural;
    }
    const free = CATEGORICAL_PALETTE.find((c) => !used.has(c));
    const color = free ?? natural; // more values than palette slots: reuse unavoidable
    used.add(color);
    return color;
  });
}

// ── Score-based ring color (#1768) ──────────────────────────────────────────
// EndDistributionCard's x-axis is an avg-score-per-arrow scale, so its bars
// are colored by SCORE QUALITY (WA ring bands: gold/red/blue/black/white),
// not by an unrelated categorical dimension. Reuses `scoreRingColor` — the
// single source of truth for score→ring-color already used by arrow dots,
// heatmaps and ScatterLegend (lib/scoring.js) — instead of duplicating the
// gold/red/blue/black/white band table here.
//
// The end-distribution histogram merges ends across every round format (WA,
// NFAA, 3D, field, …) with no per-bin face context, so there is no single
// "max arrow score" to normalise against per bin. We fall back to the same
// 10-ring WA scale the rest of the app defaults to when no face is known
// (see `maxArrowScoreForFace`'s default), clamping to whole-number score
// bands 1–10; values outside that range (e.g. a 3D face's 11/12 inner-vital
// rings) collapse to the nearest edge band (gold for "very high", vellum/
// white for "very low") rather than falling through to "miss".
//
// #1861 — this is the ONLY avg-score→ring-colour mapping. ShotCalendar's
// scoreBand/BAND_COLOR and RoundStatStrip's floor-based avgZoneColor drifted
// (8.5 was gold here, red there); both now route through here. One rounding
// policy: round to the nearest whole score, then clamp. Two sanctioned
// options instead of second tables:
//   face — pass the round's target face so non-WA ring maps stay accurate
//          (e.g. NFAA's white centre: 5 → vellum), via `scoreRingColor`.
//   text — printed numbers swap --gold for the theme-aware --gold-text
//          (legible on light surfaces; aliases --gold on the dark theme).
//          Fills (bars, dots, swatches) keep --gold.
export function avgScoreRingColor(avgScorePerArrow, { face, text = false } = {}) {
  const n = Number(avgScorePerArrow);
  if (!Number.isFinite(n)) return "var(--miss)";
  const clamped = Math.max(1, Math.min(10, Math.round(n)));
  const color = scoreRingColor(clamped, face);
  return text && color === "var(--gold)" ? "var(--gold-text)" : color;
}
